import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useSourceAudioActions, useSourceAudioPlayback } from "../hooks/hooks";
import { formatBytes, formatDuration, formatRetainedAt, originLabel } from "./sourceAudio";
import s from "./SourceAudioPanel.module.scss";

// The audio a doc kept from its source (TT-1190), owner-only: what it is, a
// player, and a way to delete it. `sourceAudio` is the doc read's
// `source.audio` (TT-1273). `resolveDelete(docId | null, response)`: the
// id on success, null on failure, and the response either way so a refusal can
// be worded.
const SourceAudioPanel = ({ docId, docTitle, sourceAudio, resolveDelete }) => {
  const { setDeletingAudio, deleteAudioConfirm } = useSourceAudioActions(resolveDelete);
  const playback = useSourceAudioPlayback(docId);
  // Play is replaced by the player; keyboard focus follows it rather than
  // dropping to the page.
  const playerRef = useRef(null);
  const hasPlayer = Boolean(playback.src);
  useEffect(() => {
    if (hasPlayer) playerRef.current?.focus();
  }, [hasPlayer]);

  const facts = [
    ["Size", formatBytes(sourceAudio.bytes)],
    ["Length", formatDuration(sourceAudio.duration_s)],
    ["Source", originLabel(sourceAudio.origin)],
    ["Kept", formatRetainedAt(sourceAudio.retained_at)],
  ];

  return (
    <section className={s.panel} aria-label="Audio recording">
      <div className={s.heading}>
        <GraphicEqIcon className={s.icon} />
        Audio recording
      </div>
      <dl className={s.facts}>
        {facts.map(([label, value]) => (
          <div key={label} className={s.fact}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {playback.src ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- a meeting's own recording; the transcript is the doc
        <audio
          ref={playerRef}
          tabIndex={0}
          className={s.player}
          controls
          autoPlay
          preload="metadata"
          src={playback.src}
          onError={playback.onError}
          onLoadedMetadata={playback.onLoadedMetadata}
          aria-label="Play audio recording"
        />
      ) : (
        <button className={s.playBtn} onClick={playback.play} disabled={playback.state === "loading"}>
          <PlayArrowIcon /> {playback.state === "loading" ? "Loading…" : "Play"}
        </button>
      )}
      {playback.state === "error" && (
        <span className={s.playError} role="alert">
          The recording could not be played. Try again in a moment.
        </span>
      )}
      <button className={s.deleteBtn} onClick={() => setDeletingAudio({ docId, title: docTitle })}>
        <DeleteOutlineIcon /> Delete audio
      </button>
      {deleteAudioConfirm}
    </section>
  );
};

SourceAudioPanel.propTypes = {
  docId: PropTypes.string.isRequired,
  docTitle: PropTypes.string,
  sourceAudio: PropTypes.shape({
    bytes: PropTypes.number,
    duration_s: PropTypes.number,
    origin: PropTypes.string,
    retained_at: PropTypes.string,
  }).isRequired,
  resolveDelete: PropTypes.func.isRequired,
};

export default SourceAudioPanel;
